/**
 * Course API Composable
 * Handles all API calls to FastAPI backend for course data
 */

import { ref, type Ref } from 'vue'

// API Base URL - Use relative path to leverage Nginx proxy
const API_BASE = '/api/v1/academy'

// Types
export interface TabConfig {
  id: string
  title: string
  subtitle: string
  icon: string
  visible: boolean
  locked: boolean
  order: number
}

export interface CourseTabConfig {
  tabs: TabConfig[]
  default_tab: string
}

export interface CourseData {
  id: number
  fullname: string
  shortname: string
  summary: string
  category: number
  category_name: string
  format: string
  startdate: number
  enddate: number
  visible: number
  enablecompletion: number
  is_enrolled: boolean
  completion_percentage: number
  tab_config?: CourseTabConfig | null
  age?: string | null
  level?: number | null
  duration?: number | null
  course_image?: string | null
}

export interface CourseSection {
  id: number
  name: string
  summary: string
  visible: number
  section: number
  activities: Activity[]
}

export interface Activity {
  id: number
  name: string
  modname: string
  description: string
  url: string
  completion?: number
}

export interface CourseProgress {
  overall: number
  assignments: { done: number; total: number; percentage: number }
  quizzes: { done: number; total: number; percentage: number }
  videos: { done: number; total: number; percentage: number }
  xp_points: number
  current_level: number
}

export interface InstructorData {
  name: string
  title: string
  photo: string
  initials: string
}

// Composable
export function useCourseAPI() {
  const loading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Fetch course data
   */
  async function fetchCourse(courseId: number): Promise<CourseData | null> {
    loading.value = true
    error.value = null

    try {
      const response = await fetch(`${API_BASE}/courses/${courseId}`)

      if (!response.ok) {
        throw new Error(`Failed to fetch course: ${response.statusText}`)
      }

      const data = await response.json()
      return data
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
      console.error('Error fetching course:', err)
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch course sections
   */
  async function fetchCourseSections(courseId: number): Promise<CourseSection[]> {
    loading.value = true
    error.value = null

    try {
      const response = await fetch(`${API_BASE}/courses/${courseId}/sections`)

      if (!response.ok) {
        throw new Error(`Failed to fetch sections: ${response.statusText}`)
      }

      const data = await response.json()
      // API returns { course_name, course_id, sections } - extract sections array
      const sections = data.sections || data
      console.log('📚 [API] Fetched sections for course', courseId, ':', Array.isArray(sections) ? sections.length : 0)
      return Array.isArray(sections) ? sections : []
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Unknown error'
      console.error('Error fetching sections:', err)
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * Get course metadata (price, duration, level, etc.)
   * Now pulls from database fields: age, level, duration
   */
  function getCourseMetadata(course: CourseData) {
    const courseName = course.fullname.toLowerCase()

    // Get data from database fields
    const dbLevel = course.level || 1
    const dbAge = course.age || 'All Ages'
    const dbDuration = course.duration || 1

    // Format duration (assume number is in months)
    const durationText = dbDuration === 1 ? '1 Month' : `${dbDuration} Months`

    // Determine difficulty level based on database level
    const difficultyMap: Record<number, string> = {
      1: 'Beginner',
      2: 'Intermediate',
      3: 'Advanced',
      4: 'Expert'
    }
    const difficulty = difficultyMap[dbLevel] || 'Beginner'

    // Determine course type and defaults
    let metadata = {
      type: 'professional',
      level: dbLevel,
      levelTitle: `Level ${dbLevel} Course`,
      duration: durationText,
      ageRange: dbAge,
      difficulty: difficulty,
      price: '₹15,000',
      originalPrice: '₹20,000',
      enrolled: '500'
    }

    // Kids courses
    if (courseName.includes('junior') || courseName.includes('kids')) {
      metadata = {
        ...metadata,
        type: 'kids',
        levelTitle: `Kids Course - Level ${dbLevel}`,
        price: '₹5,000',
        originalPrice: '₹7,500',
        enrolled: '800'
      }
    }
    // Youth courses
    else if (courseName.includes('youth')) {
      metadata = {
        ...metadata,
        type: 'youth',
        levelTitle: `Youth Course - Level ${dbLevel}`,
        price: '₹45,000',
        originalPrice: '₹60,000',
        enrolled: '1,500'
      }
    }
    // Advanced courses
    else if (courseName.includes('superstar') || courseName.includes('advanced')) {
      metadata = {
        ...metadata,
        type: 'advanced',
        levelTitle: `Advanced Course - Level ${dbLevel}`,
        price: '₹1,00,000',
        originalPrice: '₹1,50,000',
        enrolled: '300'
      }
    }

    return metadata
  }

  /**
   * Calculate course progress
   */
  function calculateProgress(sections: CourseSection[]): CourseProgress {
    let totalActivities = 0
    let completedActivities = 0
    let assignmentTotal = 0
    let assignmentDone = 0
    let quizTotal = 0
    let quizDone = 0
    let videoTotal = 0
    let videoDone = 0

    sections.forEach(section => {
      section.activities.forEach(activity => {
        totalActivities++

        if (activity.completion === 1) {
          completedActivities++
        }

        if (activity.modname === 'assign') {
          assignmentTotal++
          if (activity.completion === 1) assignmentDone++
        } else if (activity.modname === 'quiz') {
          quizTotal++
          if (activity.completion === 1) quizDone++
        } else if (activity.modname === 'page' || activity.modname === 'resource') {
          videoTotal++
          if (activity.completion === 1) videoDone++
        }
      })
    })

    const overall = totalActivities > 0 ? Math.round((completedActivities / totalActivities) * 100) : 0
    const xp_points = (completedActivities * 50) + (assignmentDone * 100) + (quizDone * 150)
    const current_level = Math.max(1, Math.floor(xp_points / 1000) + 1)

    return {
      overall,
      assignments: {
        done: assignmentDone,
        total: assignmentTotal,
        percentage: assignmentTotal > 0 ? Math.round((assignmentDone / assignmentTotal) * 100) : 0
      },
      quizzes: {
        done: quizDone,
        total: quizTotal,
        percentage: quizTotal > 0 ? Math.round((quizDone / quizTotal) * 100) : 0
      },
      videos: {
        done: videoDone,
        total: videoTotal,
        percentage: videoTotal > 0 ? Math.round((videoDone / videoTotal) * 100) : 0
      },
      xp_points,
      current_level
    }
  }

  /**
   * Get instructors/mentors data
   */
  function getInstructors(): InstructorData[] {
    return [
      {
        name: 'Saudhamini AN',
        initials: 'SA',
        title: 'Lead AI Mentor',
        photo: 'https://imagedelivery.net/GSYWs1kbpG8XypJe3mjC0Q/5147a19d-58ed-4e80-5810-f949cbb54700/public'
      },
      {
        name: 'Vineeth P',
        initials: 'VP',
        title: 'Senior AI Instructor',
        photo: 'https://imagedelivery.net/GSYWs1kbpG8XypJe3mjC0Q/99f02768-74cc-4e93-08e8-f6da8d970200/public'
      },
      {
        name: 'Binuraj E',
        initials: 'BE',
        title: 'AI Education Specialist',
        photo: 'https://imagedelivery.net/GSYWs1kbpG8XypJe3mjC0Q/66925523-7848-4a25-111e-97c0e3823d00/public'
      }
    ]
  }

  return {
    loading,
    error,
    fetchCourse,
    fetchCourseSections,
    getCourseMetadata,
    calculateProgress,
    getInstructors
  }
}
